import { buildExerciseTtsButtonHtml } from './inlineMarkdown.js';
import { TABLE_NO_HEADER_MARKER, isTableNoHeaderDirective } from './gapParsing.js';

export { TABLE_NO_HEADER_MARKER, isTableNoHeaderDirective };

/**
 * Exercise body storage stays marker-based (**, [$tts$], ![image](url), [gap], etc.).
 * Helpers: clipboard HTML ↔ markers, and markers ↔ visual-editor HTML.
 */

const PROTECTED_PREFIX = '\uE000';
const PROTECTED_SUFFIX = '\uE001';

function escapeHtml(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function decodeHtmlEntities(text) {
    if (!text || !/[&<]/.test(text)) return text || '';
    if (typeof document === 'undefined') {
        return String(text)
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&')
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/&quot;/gi, '"')
            .replace(/&#39;/gi, "'");
    }
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
}

function rgbToHex(rgb) {
    const m = String(rgb || '').match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (!m) return null;
    const hex = (n) => Number(n).toString(16).padStart(2, '0');
    return `#${hex(m[1])}${hex(m[2])}${hex(m[3])}`;
}

function normalizeColor(value) {
    if (!value) return null;
    const v = String(value).trim().toLowerCase();
    if (!v || v === 'inherit' || v === 'transparent' || v === 'initial') return null;
    if (v.startsWith('#')) {
        if (v.length === 4) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
        return v.length >= 7 ? v.slice(0, 7) : v;
    }
    if (v.startsWith('rgb')) return rgbToHex(v);
    return null;
}

function styleAttr(el) {
    return el?.getAttribute?.('style') || '';
}

/** Google Docs wraps whole paragraphs in <b style="font-weight:normal">. That is not bold. */
function styleDeclaresNormalWeight(el) {
    return /font-weight\s*:\s*(normal|[1-4]00)\b/i.test(styleAttr(el));
}

function styleDeclaresNormalItalic(el) {
    return /font-style\s*:\s*normal\b/i.test(styleAttr(el));
}

function isBold(el) {
    if (!el || el.nodeType !== 1) return false;
    if (styleDeclaresNormalWeight(el)) return false;
    const w = (el.style?.fontWeight || '').toString();
    if (w === 'bold' || w === 'bolder') return true;
    const n = parseInt(w, 10);
    if (Number.isFinite(n) && n >= 600) return true;
    if (/font-weight\s*:\s*(bold|bolder|[6-9]00)\b/i.test(styleAttr(el))) return true;
    return el.tagName === 'STRONG' || el.tagName === 'B';
}

function isItalic(el) {
    if (!el || el.nodeType !== 1) return false;
    if (styleDeclaresNormalItalic(el)) return false;
    if (el.tagName === 'EM' || el.tagName === 'I') return true;
    const fs = (el.style?.fontStyle || '').toLowerCase();
    if (fs === 'italic' || fs === 'oblique') return true;
    const styleAttr = el.getAttribute?.('style') || '';
    return /font-style\s*:\s*(italic|oblique)/i.test(styleAttr);
}

function isUnderline(el) {
    const td = (el.style?.textDecoration || el.style?.textDecorationLine || '').toLowerCase();
    if (td.includes('underline')) return true;
    return el.tagName === 'U';
}

function wrapMarkers(text, wrappers) {
    if (!text || !wrappers?.length) return text || '';

    /**
     * contentEditable often leaves `<br>` *inside* `<strong>`/`<em>` when Enter is
     * pressed at the end of a bold line. Naively wrapping the whole child text
     * yields `**LEVEL 1:\n**[next]` (closing ** jumps to the next line).
     * Wrap each non-empty line separately instead.
     */
    const applyPair = (chunk, before, after) => {
        if (!chunk) return chunk;
        if (!String(chunk).includes('\n')) return `${before}${chunk}${after}`;
        return String(chunk)
            .split('\n')
            .map((line) => {
                if (!String(line).replace(/\u00a0/g, ' ').trim()) return line;
                return `${before}${line}${after}`;
            })
            .join('\n');
    };

    let out = text;
    wrappers.forEach(({ before, after }) => {
        if (!out) return;
        out = applyPair(out, before, after);
    });
    return out;
}

function parseHexChannels(hex) {
    const m = String(hex || '').match(/^#([0-9a-f]{6})$/i);
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Docs/Word/editor body ink — not a teacher-chosen highlight. */
function isDefaultBodyInk(hex) {
    const ch = parseHexChannels(hex);
    if (!ch) return false;
    const max = Math.max(ch.r, ch.g, ch.b);
    const min = Math.min(ch.r, ch.g, ch.b);
    // Near-black (#000, #0f1115, #222, …)
    if (max <= 50) return true;
    // Neutral gray body text: #333333, #434343, #525266, #595959, #666666.
    if ((max - min) <= 28 && max <= 160 && min >= 40) return true;
    return false;
}

function isDefaultBodyFill(hex) {
    const ch = parseHexChannels(hex);
    if (!ch) return false;
    return ch.r >= 245 && ch.g >= 245 && ch.b >= 245;
}

/** Pale Docs/Word theme fills (comment yellow, selection blue), not a word highlight. */
function isPaleThemeFill(hex) {
    if (isDefaultBodyFill(hex)) return true;
    const ch = parseHexChannels(hex);
    if (!ch) return false;
    const max = Math.max(ch.r, ch.g, ch.b);
    const min = Math.min(ch.r, ch.g, ch.b);
    return min >= 190 && max >= 230 && (max - min) <= 80;
}

/** Matching-columns / bilingual pair (`Left // Right`), not `http://`. */
function lineLooksLikeSlashPair(line) {
    return /(?<!:)\/\//.test(String(line || ''));
}

/** Antonym / bilingual lines with a spaced dash (`left – right`, `left — right`, `left - right`). */
function lineLooksLikeDashPair(line) {
    return /\s[–—−]\s/.test(String(line || '')) || /\s-\s/.test(String(line || ''));
}

/**
 * Word/Docs often wrap mid-sentence with a newline before a lowercase letter.
 * Do not join Matching Columns / vocab / antonym lines even when the next
 * line starts with a lowercase word (Latin or Cyrillic).
 */
function shouldJoinSoftLineBreak(prevChunk, nextChunk) {
    const next = String(nextChunk || '').replace(/^\s+/, '');
    if (!next || !/^\p{Ll}/u.test(next)) return false;
    const prevLine = String(prevChunk || '').split('\n').pop() || '';
    const nextLine = next.split('\n')[0] || '';
    if (lineLooksLikeSlashPair(prevLine) || lineLooksLikeSlashPair(nextLine)) return false;
    if (lineLooksLikeDashPair(prevLine) || lineLooksLikeDashPair(nextLine)) return false;
    // A finished prompt (ellipsis or sentence end) is its own line, not a soft wrap.
    if (/(\.{2,}|…|[.!?])\s*$/.test(prevLine.trim())) return false;
    return true;
}

/**
 * Lettered/numbered prompt lines and slash-gap prompts (e.g. `a. When / it?`).
 * Docs/Word often italicize or bold these whole lines (or the first word) — never keep that.
 */
function isClipboardPromptLine(text) {
    const t = String(text || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
    if (!t) return false;
    if (/^[a-zA-Z]\.\s+\S/.test(t) || /^\d{1,2}\.\s+\S/.test(t)) return true;
    // Lone list marker chrome: `a.` / `12.`
    if (/^[a-zA-Z]\.$/.test(t) || /^\d{1,2}\.$/.test(t)) return true;
    // Gap slash prompts: `When / it?`, `Who / you / with?`
    if (/\s\/\s/.test(t)) return true;
    return false;
}

function clipboardBlockPlainText(el) {
    const block = el?.closest?.('p, div, li, h1, h2, h3, h4, h5, h6');
    if (!block) return '';
    return String(block.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Docs/Word often paint font-weight/italic on a whole paragraph or a comma list.
 * Only keep that as markers when it covers a short local run (a word or short phrase).
 */
function emphasisIsLocal(text) {
    const t = String(text || '').replace(/\s+/g, ' ').trim();
    if (!t) return false;
    if (t.includes('//')) return false;
    if (isClipboardPromptLine(t)) return false;
    if ((t.match(/,/g) || []).length >= 2) return false;
    if (t.length > 80) return false;
    return true;
}

/** Clipboard: keep bold/italic only for true local runs, never for prompt-line chrome. */
function keepClipboardEmphasis(el, childText) {
    if (!emphasisIsLocal(childText)) return false;
    if (isClipboardPromptLine(clipboardBlockPlainText(el))) return false;
    return true;
}

/** Safety net: strip leftover * / ** on lettered slash-prompt lines after HTML→markers. */
function stripSpuriousPasteEmphasisMarkers(text) {
    return String(text || '')
        .split('\n')
        .map((line) => {
            if (!/[*]/.test(line)) return line;
            const plain = line.replace(/\*+/g, '');
            if (!isClipboardPromptLine(plain.trim())) return line;
            // Drop indent left over from ` *b. …` after stars are removed.
            return plain.replace(/^[ \t]+/, '');
        })
        .join('\n');
}

/** Trim spaces inside ** ** / * * and flatten Docs double-wrap (****word****). */
function cleanupEmphasisMarkers(text) {
    let s = String(text || '');
    let prev;
    do {
        prev = s;
        s = s.replace(/\*\*\*\*/g, '**');
        s = s.replace(/\*\*\s+\*\*/g, ' ');
        // Keep leading/trailing spaces outside **…** (ProgressMe often puts the
        // space inside <strong>swim </strong>very → must become **swim** very).
        s = s.replace(/\*\*([^*]+?)\*\*/g, (_, inner) => {
            const raw = String(inner).replace(/\u00a0/g, ' ');
            const lead = (raw.match(/^\s*/) || [''])[0];
            const trail = (raw.match(/\s*$/) || [''])[0];
            const trimmed = raw.trim();
            return trimmed ? `${lead}**${trimmed}**${trail}` : `${lead}${trail}`;
        });
        // Merge adjacent identical color spans: {c:#x}can{/c}{c:#x} {/c} → {c:#x}can {/c}
        s = s.replace(/\{c:([^}]+)\}([^{]*?)\{\/c\}\s*\{c:\1\}([^{]*?)\{\/c\}/g, '{c:$1}$2$3{/c}');
    } while (s !== prev);
    return s;
}

function isMarkdownLinkOnly(text) {
    return /^\[[^\]]+\]\(https?:\/\/[^)]+\)$/.test(String(text || '').trim());
}

/** Drop font-size and a color/background that wraps an entire pasted line. */
function stripPastedThemeMarkers(text) {
    let s = String(text || '').replace(/\{s:[^}]+\}([\s\S]*?)\{\/s\}/g, '$1');
    const unwrap = (line, openRe) => {
        let next = line;
        let prev;
        do {
            prev = next;
            next = next.replace(openRe, (m, lead, mid, tail) => {
                if (/\{(?:c|bg|s):/.test(mid) || /\{\/(?:c|bg|s)\}/.test(mid)) return m;
                return `${lead}${mid}${tail}`;
            });
        } while (next !== prev);
        return next;
    };
    return s.split('\n').map((line) => {
        let next = unwrap(line, /^(\s*)\{c:[^}]+\}([\s\S]*)\{\/c\}(\s*)$/);
        next = unwrap(next, /^(\s*)\{bg:[^}]+\}([\s\S]*)\{\/bg\}(\s*)$/);
        return next;
    }).join('\n');
}

/**
 * Collapse Word/Docs soft-wrap line breaks in pasted marker text.
 * Only for clipboard paste — not for Visual editor round-trips (Enter must stay).
 *
 * e.g. `"I'm\ncurrently a..."` → `"I'm currently a..."`
 */
export function normalizePastedExerciseText(text) {
    let s = String(text || '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');

    let prev;
    do {
        prev = s;
        s = s.replace(/([^\s\n])[ \t]*\n[ \t]*(\p{Ll})/gu, (match, a, b, offset) => {
            const prevLineStart = s.lastIndexOf('\n', offset - 1) + 1;
            const prevLine = s.slice(prevLineStart, offset + 1);
            const bIndex = offset + match.length - b.length;
            const nextNl = s.indexOf('\n', bIndex);
            const nextLine = s.slice(bIndex, nextNl === -1 ? s.length : nextNl);
            if (!shouldJoinSoftLineBreak(prevLine, nextLine)) return `${a}\n${b}`;
            return `${a} ${b}`;
        });
    } while (s !== prev);

    return stripSpuriousPasteEmphasisMarkers(
        s
            .replace(/[ \t]+\n/g, '\n')
            .replace(/[ \t]{2,}/g, ' ')
            .replace(/\n{3,}/g, '\n\n')
            // Quoted prompt lines often arrive with empty Word paragraphs between them
            .replace(/(["”»][^\n]*)\n\n+(["“«])/g, '$1\n$2')
            .trim()
    );
}

function peekPrecedingText(node) {
    let n = node?.previousSibling;
    while (n) {
        if (n.nodeType === Node.TEXT_NODE) {
            const t = String(n.nodeValue || '').replace(/\u00a0/g, ' ');
            if (t.trim()) return t.replace(/\s+$/, '');
            n = n.previousSibling;
            continue;
        }
        if (n.nodeType === Node.ELEMENT_NODE) {
            const tag = n.tagName;
            if (tag === 'BR' || tag === 'HR') return '';
            if (tag === 'SCRIPT' || tag === 'STYLE') {
                n = n.previousSibling;
                continue;
            }
            if (['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE', 'UL', 'OL'].includes(tag)) {
                return '';
            }
            const t = String(n.textContent || '').replace(/\u00a0/g, ' ');
            if (t.trim()) return t.replace(/\s+$/, '');
        }
        n = n.previousSibling;
    }
    return '';
}

function peekFollowingText(node) {
    let n = node?.nextSibling;
    while (n) {
        if (n.nodeType === Node.TEXT_NODE) {
            const t = String(n.nodeValue || '').replace(/\u00a0/g, ' ');
            if (t.trim()) return t.replace(/^\s+/, '');
            n = n.nextSibling;
            continue;
        }
        if (n.nodeType === Node.ELEMENT_NODE) {
            const tag = n.tagName;
            if (tag === 'BR' || tag === 'HR') return '';
            if (tag === 'SCRIPT' || tag === 'STYLE') {
                n = n.nextSibling;
                continue;
            }
            // Don't look into the next block sibling — that is a real paragraph break.
            if (['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE', 'UL', 'OL'].includes(tag)) {
                return '';
            }
            const t = String(n.textContent || '').replace(/\u00a0/g, ' ');
            if (t.trim()) return t.replace(/^\s+/, '');
        }
        n = n.nextSibling;
    }
    return '';
}

/**
 * Convert clipboard / contentEditable HTML into exercise marker text.
 * @param {string} html
 * @param {{ fromClipboard?: boolean }} [options]
 */
export function htmlToExerciseMarkers(html, options = {}) {
    const fromClipboard = !!options.fromClipboard;
    if (!html) return '';
    if (typeof document === 'undefined') {
        const plain = decodeHtmlEntities(String(html).replace(/<[^>]+>/g, ''));
        return fromClipboard ? normalizePastedExerciseText(plain) : plain;
    }

    const root = document.createElement('div');
    root.innerHTML = String(html)
        .replace(/<!--\[if[\s\S]*?<!\[endif]-->/gi, '')
        .replace(/<!--[\s\S]*?-->/g, '');

    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            return node.nodeValue.replace(/\u00a0/g, ' ');
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return '';

        const el = node;
        const tag = el.tagName;

        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'META' || tag === 'LINK' || tag === 'XML') {
            return '';
        }

        if (tag === 'IMG') {
            const src = el.getAttribute('src') || '';
            if (!src) return '';
            const alt = el.getAttribute('alt') || 'image:50';
            const safeAlt = /^image(?::.+)?$/i.test(alt.trim()) ? alt.trim() : 'image:50';
            return `![${safeAlt}](${src})`;
        }

        if (tag === 'AUDIO' || tag === 'IFRAME') {
            return el.outerHTML;
        }

        if (tag === 'SUP' || tag === 'SUB') {
            const inner = Array.from(el.childNodes).map(walk).join('');
            if (!fromClipboard) return inner;
            const t = String(inner || '').trim();
            if (/^\d+$/.test(t) || /^(st|nd|rd|th)$/i.test(t)) return '';
            return inner;
        }

        if (tag === 'BR') {
            if (el.getAttribute?.('data-ex-hard') === '1') return '\n';
            // Word/Docs soft wraps often use <br> before a lowercase continuation.
            if (fromClipboard) {
                const next = peekFollowingText(el);
                const prevText = peekPrecedingText(el);
                if (shouldJoinSoftLineBreak(prevText, next)) return ' ';
            }
            // Structural editor blocks emit their own trailing newline. markersToEditorHtml
            // places a <br> after a block when another marker line follows; counting both
            // merges/edit-shifts following gap lines or creates a phantom blank line.
            let prev = el.previousSibling;
            while (prev?.nodeType === Node.TEXT_NODE && !String(prev.nodeValue || '').trim()) {
                prev = prev.previousSibling;
            }
            if (
                prev?.nodeType === Node.ELEMENT_NODE
                && (
                    prev.getAttribute?.('data-ex-numbered') === '1'
                    || prev.getAttribute?.('data-ex-note') === '1'
                    || ['UL', 'OL', 'TABLE'].includes(prev.tagName)
                )
            ) {
                return '';
            }
            return '\n';
        }
        if (tag === 'HR') return '\n---\n';

        // Visual Note callout → :::note … :::
        if (el.getAttribute?.('data-ex-note') === '1') {
            const title = (el.getAttribute('data-ex-note-title') || 'Note').trim() || 'Note';
            const bodyParts = Array.from(el.childNodes)
                .filter((n) => !(n.nodeType === 1 && n.getAttribute?.('data-ex-note-label') === '1'))
                .map(walk)
                .join('');
            const titleSuffix = title.toLowerCase() === 'note' ? '' : ` ${title}`;
            const body = bodyParts.replace(/^\n+/, '').replace(/\n+$/, '');
            // Trailing newline is the block boundary. The immediately following editor
            // <br> is ignored above, so this does not create an extra blank line.
            return `:::note${titleSuffix}\n${body}\n:::\n`;
        }
        if (el.getAttribute?.('data-ex-note-label') === '1') {
            return '';
        }

        // Circled numbered list (Visual) → 1. / 2. / 3. markers
        if (el.getAttribute?.('data-ex-numbered') === '1') {
            const items = Array.from(el.children).filter((c) => c.getAttribute?.('data-ex-num'));
            const linesOut = items.flatMap((item) => {
                const n = item.getAttribute('data-ex-num') || '1';
                const textEl = item.querySelector('[data-ex-numbered-text]');
                const textWithBlockBoundaries = textEl
                    ? Array.from(textEl.childNodes).map((child, index) => {
                        const value = walk(child);
                        const isNestedLine = child.nodeType === Node.ELEMENT_NODE
                            && ['DIV', 'P'].includes(child.tagName);
                        return isNestedLine && index > 0 ? `\n${value}` : value;
                    }).join('')
                    : '';
                const logicalLines = textWithBlockBoundaries
                    .replace(/^\n+|\n+$/g, '')
                    .split(/\n+/);
                const first = stripNumberedListChromeMarkers((logicalLines.shift() || '').trim());
                // Enter inside the flex list text creates a nested DIV/BR. Promote every
                // continuation back to a root marker line instead of folding it into the
                // numbered sentence (which previously put [] on the same line).
                return [
                    `${n}. ${first}`,
                    ...logicalLines.map((line) => stripNumberedListChromeMarkers(line.trim())),
                ];
            });
            return `${linesOut.join('\n')}\n`;
        }
        if (el.getAttribute?.('data-ex-num-badge') === '1') {
            return '';
        }
        // Pill chrome (bg/color/weight) is presentational — never save as {bg}/{c}/**
        if (el.getAttribute?.('data-ex-numbered-text') === '1') {
            return Array.from(el.childNodes).map(walk).join('');
        }

        // Skip walking into nested table cells twice — handled at TABLE
        if (tag === 'THEAD' || tag === 'TBODY' || tag === 'TFOOT' || tag === 'TR' || tag === 'TD' || tag === 'TH') {
            return Array.from(el.childNodes).map(walk).join('');
        }

        const childText = Array.from(el.childNodes).map(walk).join('');

        // Explicit emphasis tags (Visual editor uses <em>/<i>).
        // Skip when Docs set font-weight/style normal, or the tag wraps a whole line/list.
        if (tag === 'EM' || tag === 'I') {
            if (styleDeclaresNormalItalic(el)) return childText;
            if (fromClipboard && !keepClipboardEmphasis(el, childText)) return childText;
            return wrapMarkers(childText, [{ before: '*', after: '*' }]);
        }
        if (tag === 'STRONG' || tag === 'B') {
            if (styleDeclaresNormalWeight(el)) return childText;
            if (fromClipboard && !keepClipboardEmphasis(el, childText)) return childText;
            return wrapMarkers(childText, [{ before: '**', after: '**' }]);
        }

        if (tag === 'FONT') {
            const color = normalizeColor(el.getAttribute('color') || el.style?.color);
            const wrappers = [];
            if (color && !isDefaultBodyInk(color)) wrappers.push({ before: `{c:${color}}`, after: '{/c}' });
            if ((!fromClipboard || keepClipboardEmphasis(el, childText)) && isBold(el)) {
                wrappers.push({ before: '**', after: '**' });
            }
            if ((!fromClipboard || keepClipboardEmphasis(el, childText)) && isItalic(el)) {
                wrappers.push({ before: '*', after: '*' });
            }
            if (isUnderline(el) && !(fromClipboard && isMarkdownLinkOnly(childText))) {
                wrappers.push({ before: '<u>', after: '</u>' });
            }
            return wrapMarkers(childText, wrappers);
        }

        if (
            (tag === 'SPAN' || tag === 'BUTTON')
            && (el.getAttribute('data-ex-marker') === 'tts' || el.classList?.contains('ex-tts-btn'))
        ) {
            const spoken = el.getAttribute('data-tts')
                || el.getAttribute('title')
                || childText.replace(/^🔊\s*/, '');
            return `[$${spoken}$]`;
        }
        if (tag === 'SPAN' && el.getAttribute('data-ex-marker') === 'raw') {
            return el.getAttribute('data-raw') || childText;
        }
        if (tag === 'SPAN' && el.getAttribute('data-ex-marker') === 'ai-check') {
            return el.getAttribute('data-raw') || '{ai:check}';
        }
        if (el.getAttribute?.('data-ex-blur') === '1') {
            return `||${childText}||`;
        }

        if (tag === 'UL') {
            return `${Array.from(el.children)
                .filter((c) => c.tagName === 'LI')
                .map((li) => `• ${walk(li).trim()}`)
                .join('\n')}\n`;
        }
        if (tag === 'OL') {
            let i = 1;
            return `${Array.from(el.children)
                .filter((c) => c.tagName === 'LI')
                .map((li) => `${i++}. ${walk(li).trim()}`)
                .join('\n')}\n`;
        }
        if (tag === 'LI') return childText;

        if (tag === 'TABLE') {
            const trs = Array.from(el.querySelectorAll('tr'));
            const rows = trs.map((tr) => {
                const cells = Array.from(tr.children)
                    .filter((c) => c.tagName === 'TD' || c.tagName === 'TH')
                    .map((c) => walk(c).replace(/\n+/g, ' ').trim());
                return `| ${cells.join(' | ')} |`;
            });
            if (rows.length >= 1) {
                const firstCells = Array.from(trs[0]?.children || []).filter((c) => c.tagName === 'TD' || c.tagName === 'TH');
                const hasHeader = el.getAttribute('data-ex-no-header') !== '1'
                    && firstCells.some((c) => c.tagName === 'TH');
                const colCount = Math.max((rows[0].match(/\|/g) || []).length - 1, 1);
                const sep = `| ${Array(colCount).fill('---').join(' | ')} |`;
                const prefix = hasHeader ? '' : `${TABLE_NO_HEADER_MARKER}\n`;
                return `${prefix}${rows[0]}\n${sep}\n${rows.slice(1).join('\n')}\n`;
            }
            return `${rows.join('\n')}\n`;
        }

        if (tag === 'A') {
            const href = el.getAttribute('href') || '';
            const label = childText.replace(/<\/?u>/gi, '').trim() || href;
            if (href && /^https?:\/\//i.test(href)) return `[${label}](${href})`;
            return label;
        }

        const wrappers = [];
        const color = normalizeColor(el.style?.color);
        const bg = normalizeColor(el.style?.backgroundColor || el.style?.background);
        if (color && !isDefaultBodyInk(color)) wrappers.push({ before: `{c:${color}}`, after: '{/c}' });
        const paleFill = fromClipboard && isPaleThemeFill(bg);
        if (bg && !isDefaultBodyFill(bg) && !paleFill) wrappers.push({ before: `{bg:${bg}}`, after: '{/bg}' });
        const localEmphasis = !fromClipboard || keepClipboardEmphasis(el, childText);
        if (localEmphasis && isBold(el) && tag !== 'STRONG' && tag !== 'B') {
            wrappers.push({ before: '**', after: '**' });
        }
        if (localEmphasis && isItalic(el) && tag !== 'EM' && tag !== 'I') {
            wrappers.push({ before: '*', after: '*' });
        }
        if (isUnderline(el) && !(fromClipboard && (isMarkdownLinkOnly(childText) || tag === 'A'))) {
            wrappers.push({ before: '<u>', after: '</u>' });
        }

        let out = wrapMarkers(childText, wrappers);

        if (['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(tag)) {
            const trimmed = childText.replace(/\u00a0/g, ' ').trim();
            // Clipboard: Word inserts empty <p><br></p> between soft-wrapped lines — skip them.
            // Visual editor still needs empty blocks as intentional blank lines.
            if (fromClipboard && !trimmed) return '';
            // Word/Chrome often emit `<p>line<br></p>` / `<div>line<br></div>`. The inner
            // <br> already becomes `\n`; adding another block boundary would insert a
            // phantom blank line (e.g. between a sentence and a following `[]` gap).
            out = `${String(out).replace(/\n+$/, '')}\n`;
        }
        return out;
    };

    let result = Array.from(root.childNodes)
        .map(walk)
        .join('')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();

    result = cleanupEmphasisMarkers(result);
    if (fromClipboard) result = stripPastedThemeMarkers(result);
    return fromClipboard ? normalizePastedExerciseText(result) : result;
}

function isMdTableRow(line) {
    const t = String(line || '').trim();
    return t.startsWith('|') && t.includes('|', 1);
}

function isMdTableSep(line) {
    const t = String(line || '').trim();
    if (!isMdTableRow(t)) return false;
    return /^\|[\s|:\-]+\|$/.test(t) || t.split('|').slice(1, -1).every((c) => /^[\s:\-]+$/.test(c));
}

function parseMdTableCells(line) {
    const t = String(line || '').trim();
    return t
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((c) => c.trim());
}

function applyNestedColorBgSize(html, { color, bg, size }) {
    let s = String(html || '');
    let prev;
    do {
        prev = s;
        s = s.replace(/\{c:([^}]+)\}([^{]*?)\{\/c\}/g, color);
        s = s.replace(/\{bg:([^}]+)\}([^{]*?)\{\/bg\}/g, bg);
        s = s.replace(/\{s:([^}]+)\}([^{]*?)\{\/s\}/g, size);
    } while (s !== prev);
    return s;
}

function formatEditorInline(escapedLine, tokens) {
    let h = escapedLine
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>');
    h = applyNestedColorBgSize(h, {
        color: '<span style="color:$1">$2</span>',
        bg: '<span style="background-color:$1;padding:0 3px;border-radius:3px">$2</span>',
        size: '<span style="font-size:$1">$2</span>',
    });
    h = h.replace(
        /\|\|(.+?)\|\|/g,
        '<span style="filter:blur(3px);background:#e2e8f0;padding:0 2px" data-ex-blur="1">$1</span>'
    );

    return h.replace(
        new RegExp(`${PROTECTED_PREFIX}(\\d+)${PROTECTED_SUFFIX}`, 'g'),
        (_, idx) => tokens[Number(idx)] ?? ''
    );
}

const TABLE_CELL_STYLE = 'border:1px solid #cbd5e1;padding:6px 8px;min-width:48px;';
const TABLE_STYLE = 'border-collapse:collapse;width:100%;margin:8px 0;background:#fff;';

export { TABLE_CELL_STYLE, TABLE_STYLE };

/** Find a table from the caret, or the last table in the editor. */
export function findEditorTable(editorEl) {
    if (!editorEl) return null;
    const sel = window.getSelection();
    let node = sel?.anchorNode;
    if (node?.nodeType === 3) node = node.parentElement;
    const fromSel = node?.closest?.('table');
    if (fromSel && editorEl.contains(fromSel)) return fromSel;
    const tables = editorEl.querySelectorAll('table');
    return tables.length ? tables[tables.length - 1] : null;
}

export function addEditorTableRow(table) {
    if (!table) return false;
    const rows = table.querySelectorAll('tr');
    if (!rows.length) return false;
    const colCount = rows[rows.length - 1].querySelectorAll('th,td').length || 1;
    const tr = document.createElement('tr');
    for (let i = 0; i < colCount; i++) {
        const td = document.createElement('td');
        td.setAttribute('style', TABLE_CELL_STYLE);
        td.innerHTML = '<br>';
        tr.appendChild(td);
    }
    const body = table.tBodies?.[0] || table;
    body.appendChild(tr);
    return true;
}

export function editorTableHasHeader(table) {
    if (!table) return false;
    if (table.getAttribute('data-ex-no-header') === '1') return false;
    const first = table.querySelector('tr');
    if (!first) return false;
    return Array.from(first.children).some((c) => c.tagName === 'TH');
}

export function toggleEditorTableHeader(table) {
    if (!table) return false;
    const first = table.querySelector('tr');
    if (!first) return false;
    const turnOn = !editorTableHasHeader(table);
    const cells = Array.from(first.children).filter((c) => c.tagName === 'TD' || c.tagName === 'TH');
    cells.forEach((cell) => {
        const next = document.createElement(turnOn ? 'th' : 'td');
        next.setAttribute(
            'style',
            TABLE_CELL_STYLE + (turnOn ? 'background:#f8fafc;font-weight:700;' : '')
        );
        next.innerHTML = cell.innerHTML;
        cell.replaceWith(next);
    });
    if (turnOn) table.removeAttribute('data-ex-no-header');
    else table.setAttribute('data-ex-no-header', '1');
    return true;
}

export function addEditorTableColumn(table) {
    if (!table) return false;
    const rows = table.querySelectorAll('tr');
    if (!rows.length) return false;
    const header = editorTableHasHeader(table);
    rows.forEach((tr, idx) => {
        const isHeaderRow = header && idx === 0;
        const cell = document.createElement(isHeaderRow ? 'th' : 'td');
        cell.setAttribute(
            'style',
            TABLE_CELL_STYLE + (isHeaderRow ? 'background:#f8fafc;font-weight:700;' : '')
        );
        cell.innerHTML = isHeaderRow ? `Col${tr.querySelectorAll('th,td').length + 1}` : '<br>';
        tr.appendChild(cell);
    });
    return true;
}

function markdownTableToEditorHtml(tableLines, tokens, { header = true } = {}) {
    const rows = tableLines.filter((l) => !isMdTableSep(l)).map(parseMdTableCells);
    if (!rows.length) return '';
    const noHeaderAttr = header ? '' : ' data-ex-no-header="1"';
    let html = `<table class="ex-body-table" style="${TABLE_STYLE}"${noHeaderAttr}>`;
    rows.forEach((cells, rowIndex) => {
        html += '<tr>';
        cells.forEach((cell) => {
            const asHeader = header && rowIndex === 0;
            const tag = asHeader ? 'th' : 'td';
            const inner = formatEditorInline(escapeHtml(cell), tokens) || '<br>';
            html += `<${tag} style="${TABLE_CELL_STYLE}${asHeader ? 'background:#f8fafc;font-weight:700;' : ''}">${inner}</${tag}>`;
        });
        html += '</tr>';
    });
    html += '</table>';
    return html;
}

const NOTE_EDITOR_STYLE =
    'margin:10px 0;padding:10px 12px 10px 14px;border:1px solid #93c5fd;border-left:4px solid #2563eb;border-radius:8px;background:#eff6ff;color:#1e3a5f;';

/**
 * Match a markdown-style numbered list line: `1. text` or `1 text` (period optional).
 * Uses 1–2 digits so years like `2010 …` at line start are not treated as list markers.
 * Leading inline wrappers (`{c:…}`, `{bg:…}`, `**`) are ignored for the number, then
 * re-applied to the remainder so teacher colors stay on the sentence.
 */
const NUMBERED_LEADING_WRAPPER = /^(?:\{(?:c|bg|s):[^}]+\}|\*\*|__|<u>)/i;

export function matchNumberedListLine(line) {
    const raw = String(line || '');
    const opens = [];
    let rest = raw;
    while (true) {
        const wrap = rest.match(NUMBERED_LEADING_WRAPPER);
        if (!wrap) break;
        opens.push(wrap[0]);
        rest = rest.slice(wrap[0].length);
    }
    const m = rest.match(/^\s*(\d{1,2})\.?\s+(.*)$/);
    if (!m) return null;
    let text = m[2];
    for (let i = opens.length - 1; i >= 0; i--) {
        text = `${opens[i]}${text}`;
    }
    return { num: m[1], text: stripDanglingLeadingEmphasis(stripNumberedListChromeMarkers(text)) };
}

/** Default colors of the circled-number pill — never persist these as content markers. */
const NUMBERED_PILL_BG = '#f0f2f4';
const NUMBERED_PILL_COLOR = '#1e293b';

function escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function removeSpecificWrapperPairs(text, openTag, closeTag) {
    const openRe = new RegExp(escapeRegExp(openTag), 'gi');
    let s = String(text || '');
    let opens = 0;
    s = s.replace(openRe, () => {
        opens += 1;
        return '';
    });
    const closeLc = closeTag.toLowerCase();
    while (opens > 0) {
        const idx = s.toLowerCase().lastIndexOf(closeLc);
        if (idx === -1) break;
        s = s.slice(0, idx) + s.slice(idx + closeTag.length);
        opens -= 1;
    }
    return s;
}

/**
 * Strip leaked numbered-list pill styles ({bg:#f0f2f4} / {c:#1e293b}) and extra **
 * from item text. Teacher colors other than the pill defaults are kept.
 */
export function stripNumberedListChromeMarkers(text) {
    const before = String(text || '');
    const hadChrome = /\{bg:#f0f2f4\}/i.test(before) || /\{c:#1e293b\}/i.test(before);
    let s = before;
    s = removeSpecificWrapperPairs(s, `{bg:${NUMBERED_PILL_BG}}`, '{/bg}');
    s = removeSpecificWrapperPairs(s, `{c:${NUMBERED_PILL_COLOR}}`, '{/c}');
    s = s.replace(/(?:\*\*){2,}/g, '**');
    if (hadChrome) {
        s = s.replace(/^\*\*([\s\S]*)\*\*$/, '$1');
        s = s.replace(/(?:\*\*){2,}/g, '**');
    }
    s = s.replace(/^(?:\*\*)+\s*$/g, '');
    return stripDanglingLeadingEmphasis(s);
}

/** Unmatched `**` / `*` right after `1.` (pill font-weight leak from Visual). */
function stripDanglingLeadingEmphasis(text) {
    let s = String(text || '');
    if (s.startsWith('**')) {
        const rest = s.slice(2);
        if (!rest.includes('**')) s = rest;
    }
    if (s.startsWith('*') && !s.startsWith('**')) {
        const rest = s.slice(1);
        const outsideBrackets = rest.replace(/\[[^\]]*\]/g, '');
        if (!outsideBrackets.includes('*')) s = rest;
    }
    return s;
}

const NUMBERED_LIST_STYLE =
    'list-style:none;margin:10px 0;padding:0;display:flex;flex-direction:column;gap:8px;';
const NUMBERED_ITEM_STYLE =
    'display:flex;align-items:center;gap:0.55rem;margin:0;padding:0;list-style:none;';
const NUMBERED_BADGE_STYLE =
    'flex-shrink:0;width:1.75rem;height:1.75rem;border-radius:50%;background:linear-gradient(135deg,#4361ee 0%,#7c3aed 100%);color:#fff;font-weight:800;font-size:0.85rem;display:inline-flex;align-items:center;justify-content:center;z-index:1;position:relative;line-height:1;user-select:none;';
const NUMBERED_TEXT_STYLE =
    'flex:1;margin-left:0;padding:0;background:transparent;border-radius:0;color:#1e293b;font-weight:400;min-height:1.75rem;display:block;box-sizing:border-box;';

function numberedListToEditorHtml(items, tokens) {
    let html = `<div data-ex-numbered="1" style="${NUMBERED_LIST_STYLE}">`;
    items.forEach(({ num, text }) => {
        const inner = formatEditorInline(escapeHtml(stripNumberedListChromeMarkers(text)), tokens) || '<br>';
        html +=
            `<div data-ex-num="${escapeHtml(num)}" style="${NUMBERED_ITEM_STYLE}">` +
            `<span data-ex-num-badge="1" contenteditable="false" style="${NUMBERED_BADGE_STYLE}">${escapeHtml(num)}</span>` +
            `<div data-ex-numbered-text="1" style="${NUMBERED_TEXT_STYLE}">${inner}</div>` +
            `</div>`;
    });
    html += '</div>';
    return html;
}

function noteBlockToEditorHtml(bodyLines, tokens, title = 'Note') {
    const safeTitle = escapeHtml(title || 'Note');
    const inner = bodyLines
        .map((l) => formatEditorInline(escapeHtml(l), tokens) || '<br>')
        .join('<br>');
    return (
        `<div data-ex-note="1" data-ex-note-title="${safeTitle}" style="${NOTE_EDITOR_STYLE}">` +
        `<div data-ex-note-label="1" contenteditable="false" style="font-weight:700;font-size:0.72rem;letter-spacing:0.04em;text-transform:uppercase;color:#1d4ed8;margin-bottom:4px;user-select:none">${safeTitle}</div>` +
        `${inner || '<br>'}</div>`
    );
}

/** Default visual Note block HTML (round-trips to :::note … :::). */
export function buildDefaultEditorNoteHtml(placeholder = 'Your note here') {
    return noteBlockToEditorHtml([placeholder], [], 'Note');
}

/** Default visual table HTML inserted by the toolbar (round-trips to markdown). */
export function buildDefaultEditorTableHtml(cols = 2, rows = 2, { header = true } = {}) {
    const c = Math.max(1, cols);
    const r = Math.max(1, rows);
    const noHeaderAttr = header ? '' : ' data-ex-no-header="1"';
    const firstTag = header ? 'th' : 'td';
    const firstStyle = TABLE_CELL_STYLE + (header ? 'background:#f8fafc;font-weight:700;' : '');
    let html = `<table class="ex-body-table" style="${TABLE_STYLE}"${noHeaderAttr}><tr>`;
    for (let i = 0; i < c; i++) {
        html += `<${firstTag} style="${firstStyle}">${header ? `Col${i + 1}` : '<br>'}</${firstTag}>`;
    }
    html += '</tr>';
    for (let y = 0; y < r; y++) {
        html += '<tr>';
        for (let x = 0; x < c; x++) {
            html += `<td style="${TABLE_CELL_STYLE}"><br></td>`;
        }
        html += '</tr>';
    }
    html += '</table>';
    return html;
}

/**
 * Markers → HTML for the visual contentEditable (editor chips, not Interactive render).
 */
export function markersToEditorHtml(text) {
    if (!text) return '';
    const tokens = [];
    const pushTok = (html) => {
        const i = tokens.length;
        tokens.push(html);
        return `${PROTECTED_PREFIX}${i}${PROTECTED_SUFFIX}`;
    };

    let work = String(text);
    work = work.replace(/!\[([^\]]*)]\(([^)]+)\)/g, (_, alt, url) =>
        pushTok(
            `<img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}" class="embedded-image" style="max-width:40%;height:auto;border-radius:8px;vertical-align:middle">`
        )
    );
    work = work.replace(/\[\$([\s\S]*?)\$]/g, (_, spoken) =>
        pushTok(buildExerciseTtsButtonHtml(spoken))
    );
    work = work.replace(/<audio\b[^>]*>[\s\S]*?<\/audio>/gi, (m) =>
        pushTok(
            `<span data-ex-marker="raw" data-raw="${escapeHtml(m)}" contenteditable="false" style="display:inline-block;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;padding:2px 8px;margin:2px;font-size:0.8em;color:#166534;user-select:none">🎵 audio</span>`
        )
    );
    work = work.replace(/<audio\b[^>]*\/?>/gi, (m) =>
        pushTok(
            `<span data-ex-marker="raw" data-raw="${escapeHtml(m)}" contenteditable="false" style="display:inline-block;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;padding:2px 8px;margin:2px;font-size:0.8em;color:#166534;user-select:none">🎵 audio</span>`
        )
    );
    work = work.replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, (m) =>
        pushTok(
            `<span data-ex-marker="raw" data-raw="${escapeHtml(m)}" contenteditable="false" style="display:inline-block;background:#fef3c7;border:1px solid #fde68a;border-radius:6px;padding:2px 8px;margin:2px;font-size:0.8em;color:#92400e;user-select:none">🎬 video</span>`
        )
    );
    work = work.replace(/<iframe\b[^>]*\/?>/gi, (m) =>
        pushTok(
            `<span data-ex-marker="raw" data-raw="${escapeHtml(m)}" contenteditable="false" style="display:inline-block;background:#fef3c7;border:1px solid #fde68a;border-radius:6px;padding:2px 8px;margin:2px;font-size:0.8em;color:#92400e;user-select:none">🎬 video</span>`
        )
    );
    work = work.replace(/<u>([\s\S]*?)<\/u>/gi, (_, inner) => {
        // Format emphasis inside underline before protecting (otherwise *…* stays literal)
        const formatted = formatEditorInline(escapeHtml(inner), tokens);
        return pushTok(`<u>${formatted}</u>`);
    });
    work = work.replace(/\{ai:check\}/gi, () =>
        pushTok(
            `<span data-ex-marker="ai-check" data-raw="{ai:check}" contenteditable="false" style="display:inline-block;background:#eef2ff;border:1px solid #c7d2fe;border-radius:6px;padding:2px 8px;margin:2px;font-size:0.8em;color:#4338ca;font-weight:700;user-select:none">🤖 AI check</span>`
        )
    );

    const lines = work.split('\n');
    const chunks = [];
    let i = 0;
    while (i < lines.length) {
        const noteOpen = String(lines[i] || '').trim().match(/^:::note(?:\s+(.*))?$/i);
        if (noteOpen) {
            const title = (noteOpen[1] || 'Note').trim() || 'Note';
            i++;
            const bodyLines = [];
            while (i < lines.length && String(lines[i] || '').trim() !== ':::') {
                bodyLines.push(lines[i]);
                i++;
            }
            if (i < lines.length && String(lines[i] || '').trim() === ':::') i++;
            chunks.push(noteBlockToEditorHtml(bodyLines, tokens, title));
            continue;
        }
        if (isTableNoHeaderDirective(lines[i])) {
            i++;
            while (i < lines.length && !String(lines[i] || '').trim()) i++;
            if (i < lines.length && isMdTableRow(lines[i])) {
                const tableLines = [];
                while (i < lines.length && isMdTableRow(lines[i])) {
                    tableLines.push(lines[i]);
                    i++;
                }
                chunks.push(markdownTableToEditorHtml(tableLines, tokens, { header: false }));
                continue;
            }
            // Empty directive line: keep as blank chunk (join adds one <br>), not '<br>' which becomes two.
            chunks.push(formatEditorInline(escapeHtml(TABLE_NO_HEADER_MARKER), tokens));
            continue;
        }
        if (isMdTableRow(lines[i])) {
            const tableLines = [];
            while (i < lines.length && isMdTableRow(lines[i])) {
                tableLines.push(lines[i]);
                i++;
            }
            chunks.push(markdownTableToEditorHtml(tableLines, tokens));
            continue;
        }
        const numbered = matchNumberedListLine(lines[i]);
        if (numbered) {
            const items = [];
            while (i < lines.length) {
                const m = matchNumberedListLine(lines[i]);
                if (!m) break;
                items.push(m);
                i++;
            }
            chunks.push(numberedListToEditorHtml(items, tokens));
            continue;
        }
        const h = formatEditorInline(escapeHtml(lines[i]), tokens);
        // Empty lines must be '' so chunks.join('<br>') yields a single blank (a<br><br>b),
        // not h || '<br>' which became a<br><br><br>b and reappeared after delete.
        chunks.push(h);
        i++;
    }

    return chunks.join('<br>') || '<br>';
}

export function editorHtmlToMarkers(html) {
    return htmlToExerciseMarkers(html);
}

/**
 * Prefer text/html → markers; fall back to plain text.
 * Use plainOnly for Shift+Ctrl+V / explicit plain paste.
 */
export function markersFromClipboardEvent(e, { plainOnly = false } = {}) {
    const cd = e.clipboardData;
    if (!cd) return '';
    if (plainOnly) {
        return normalizePastedExerciseText(cd.getData('text/plain') || '');
    }
    const html = cd.getData('text/html');
    if (html && /<[a-z][\s\S]*>/i.test(html)) {
        return htmlToExerciseMarkers(html, { fromClipboard: true });
    }
    return normalizePastedExerciseText(cd.getData('text/plain') || '');
}

export function insertIntoTextarea(ta, text) {
    if (!ta) return { value: text, caret: String(text).length };
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? start;
    return {
        value: ta.value.slice(0, start) + text + ta.value.slice(end),
        caret: start + text.length,
    };
}
